import React, { useState, useRef, useEffect } from "react";
import "./ProductDefinition.css";
import Page1Product from "./Page1Product";
import Page2Properties from "./Page2Properties";
import Modal from "../../../../Components/Modal/Modal";
import Page4Pictures from "./Page4Pictures";
import Page3Properties from "./Page3Properties";
import FinalStep from "./FinalStep";
import config from "../../../../config";
import { usePost } from "../../../../hooks/usePost";
import TelegramSafeArea from "../../../../Components/TelegramSafeArea";
import { IoMdArrowRoundBack, IoMdCheckmark } from "react-icons/io";
import { FiFileText, FiSliders, FiTag, FiImage, FiCheckSquare } from "react-icons/fi";

export default function ProductDefinition({ fnClose, isOpen, productId, business_id }) {
    const fileInputRef = useRef(null);
    const steps = [
        { label: "مشخصات", icon: <FiFileText size={16} /> },
        { label: "ویژگی", icon: <FiSliders size={16} /> },
        { label: "قیمت گذاری", icon: <FiTag size={16} /> },
        { label: "تصویر", icon: <FiImage size={16} /> },
        { label: "ثبت نهایی", icon: <FiCheckSquare size={16} /> }
    ];
    
    const [stepIndex, setStepIndex] = useState(0);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [isLoading, setIsLoading] = useState(productId > 0);

    const [productData, setProductData] = useState(null);
    const [propertiesData, setPropertiesData] = useState(null);
    const [pricingData, setPricingData] = useState(null);
    const [imagesData, setImagesData] = useState({ images: [] });
    const [deletedImages, setDeletedImages] = useState([]);

    const { data: commodityInfo, loading: commodityLoading } = usePost(
        productId == 0 ? null : `commodity/show/info`,
        { id_commodity: productId }
    );

    useEffect(() => {
        if (productId > 0 && commodityInfo && commodityInfo.data) {
            console.log("🔄 بارگذاری داده‌های محصول از API:", commodityInfo.data);

            const loadInitialData = async () => {
                try {
                    setIsLoading(true);

                    const initialProductData = {
                        name: commodityInfo.data.title || "",
                        category: commodityInfo.data.id_commodityGroup || "",
                        shortDesc: commodityInfo.data.extraTitle || "",
                        fullDesc: commodityInfo.data.description || "",
                        instagram: commodityInfo.data.instagram_post_url || "",
                        sku: commodityInfo.data.commodity_code || "",
                        discount: Number(commodityInfo.data.discount_percent),
                        vatPercent: Number(commodityInfo.data.VAT),
                        packagingCost: Number(commodityInfo.data.pack_cost),
                        id_commodity: productId ?? 0,
                    };
                    setProductData(initialProductData);

                    if (commodityInfo.data.properties_title) {
                        const pTitle = typeof commodityInfo.data.properties_title === 'string'
                            ? JSON.parse(commodityInfo.data.properties_title)
                            : commodityInfo.data.properties_title;
                        setPropertiesData(pTitle);
                    }

                    if (commodityInfo.data.properties) {
                        const pData = typeof commodityInfo.data.properties === 'string'
                            ? JSON.parse(commodityInfo.data.properties)
                            : commodityInfo.data.properties;
                        setPricingData(pData);
                    }

                    const loadImageData = async (mainImagePath, galleryImages) => {
                        const allImages = [];

                        const getCompleteImageUrl = (url) => {
                            if (!url) return '';
                            if (url.startsWith('http://') || url.startsWith('https://')) return url;
                            const baseURL = config.api.imageURL;
                            const cleanBaseURL = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
                            const cleanUrl = url.startsWith('/') ? url.slice(1) : url;
                            return `${cleanBaseURL}/${cleanUrl}`;
                        };

                        const getFileExtension = (url) => {
                            if (!url) return 'jpeg';
                            const fileName = url.split('/').pop();
                            const segments = fileName.split('.');
                            if (segments.length > 1) return segments.pop().toLowerCase();
                            return 'jpeg';
                        };

                        const getImageDimensions = (url) => {
                            return new Promise((resolve) => {
                                const img = new Image();
                                img.onload = () => resolve({ width: img.width, height: img.height });
                                img.onerror = () => resolve({ width: 800, height: 800 });
                                img.src = url;
                            });
                        };

                        // بارگذاری عکس اصلی
                        if (mainImagePath) {
                            const completeImageUrl = getCompleteImageUrl(mainImagePath);
                            const fileExt = getFileExtension(mainImagePath);
                            const fileName = mainImagePath.split('/').pop() || 'main.jpg';
                            const dimensions = await getImageDimensions(completeImageUrl);
                            allImages.push({
                                id: `main-1`,
                                url: completeImageUrl,
                                fileName: fileName,
                                fileSize: 0,
                                fileType: `image/${fileExt}`,
                                originalSize: 0,
                                width: dimensions.width,
                                height: dimensions.height,
                                isMain: true,
                                order: 1,
                                isNew: false
                            });
                        }

                        // بارگذاری عکس‌های گالری
                        if (galleryImages && Array.isArray(galleryImages)) {
                            for (let i = 0; i < galleryImages.length; i++) {
                                const gPath = galleryImages[i];
                                if (!gPath) continue;
                                const completeImageUrl = getCompleteImageUrl(gPath);
                                const fileExt = getFileExtension(gPath);
                                const fileName = gPath.split('/').pop() || `gallery-${i}.jpg`;
                                const dimensions = await getImageDimensions(completeImageUrl);
                                allImages.push({
                                    id: `gallery-${i}`,
                                    url: completeImageUrl,
                                    fileName: fileName,
                                    fileSize: 0,
                                    fileType: `image/${fileExt}`,
                                    originalSize: 0,
                                    width: dimensions.width,
                                    height: dimensions.height,
                                    isMain: false,
                                    order: allImages.length + 1,
                                    isNew: false
                                });
                            }
                        }

                        const mainImageIndex = allImages.findIndex(img => img.isMain) + 1 || 1;
                        const mainImageId = allImages.find(img => img.isMain)?.id || null;

                        return {
                            images: allImages, imagesCount: allImages.length,
                            mainImageIndex: mainImageIndex, mainImageId: mainImageId,
                            totalSize: allImages.reduce((total, img) => total + img.fileSize, 0)
                        };
                    };

                    const mainImage = commodityInfo.data.image_path || commodityInfo.data.picBase;
                    const galleryImages = commodityInfo.data.gallery_path || commodityInfo.data.images;

                    if (mainImage || (galleryImages && galleryImages.length > 0)) {
                        const imagesResult = await loadImageData(mainImage, galleryImages);
                        setImagesData(imagesResult);
                    }

                    // ✅ در حالت ویرایش، مراحل ۱ تا ۴ را به‌عنوان "تکمیل شده" تنظیم می‌کنیم
                    let loadedSteps = [0, 1, 2, 3];
                    setCompletedSteps(loadedSteps);

                } catch (error) {
                    console.error("❌ خطا در پردازش داده‌های محصول:", error);
                } finally {
                    setIsLoading(false);
                }
            };

            loadInitialData();
        }
    }, [commodityInfo, productId]);

    const goNext = () => setStepIndex(prev => Math.min(prev + 1, steps.length - 1));
    const goPrev = () => setStepIndex(prev => Math.max(prev - 1, 0));
    
    // --- قانون ناوبری: فقط در صورت تکمیل مراحل قبلی، اجازه پرش به جلو داده شود ---
    const goToStep = (index) => {
        // بازگشت به عقب همیشه مجاز است
        if (index < stepIndex) {
            setStepIndex(index);
            return;
        }
        // برای رفتن به جلو، بررسی می‌کنیم که تمام مراحل قبل از آن تکمیل شده باشند
        let canGoForward = true;
        for (let i = 0; i < index; i++) {
            if (!completedSteps.includes(i)) {
                canGoForward = false;
                break;
            }
        }
        
        if (canGoForward) {
            setStepIndex(index);
        } else {
            alert("لطفاً ابتدا مراحل قبلی را تکمیل کنید.");
        }
    };
    
    const handleExit = () => { if (fnClose) fnClose(); };

    const handleProductData = (data) => {
        setProductData(data);
        if (!completedSteps.includes(0)) setCompletedSteps(prev => [...prev, 0]);
        goNext();
    };

    // --- قانون اختیاری بودن مرحله 2: پشتیبانی از دیتای خالی ---
    const handlePropertiesData = (data) => {
        const safeData = data || {}; 
        if (pricingData) {
            const updatedPricingData = updatePricingDataForNewProperties(pricingData, safeData);
            setPricingData(updatedPricingData);
        }
        setPropertiesData(safeData);
        if (!completedSteps.includes(1)) setCompletedSteps(prev => [...prev, 1]);
        goNext();
    };

    const handlePropertiesDataOnPrev = (data) => {
        setPropertiesData(data);
        setStepIndex(0);
    };

    const handlePricingData = (data) => {
        setPricingData(data);
        if (!completedSteps.includes(2)) setCompletedSteps(prev => [...prev, 2]);
        goNext();
    };

    const handlePricingDataOnPrev = (data) => {
        setPricingData(data);
        setStepIndex(1);
    };

    const handleImagesDataUpdate = (data) => {
        const completeData = {
            images: data.images || [], imagesCount: data.imagesCount || 0,
            mainImageIndex: data.mainImageIndex || 1, mainImageId: data.mainImageId || null,
            totalSize: data.totalSize || 0, deletedImages: data.deletedImages || []
        };
        setImagesData(completeData);
    };
    
    const handleDeletedImagesUpdate = (newDeletedImages) => setDeletedImages(newDeletedImages);

    const handleNewProduct = () => {
        setProductData(null);
        setPropertiesData(null);
        setPricingData(null);
        setImagesData({ images: [] });
        setDeletedImages([]);
        setCompletedSteps([]);
        setStepIndex(0);
        if (fileInputRef.current) fileInputRef.current.value = '';
        alert("کالای جدید با موفقیت شروع شد! لطفا اطلاعات جدید را وارد کنید.");
    };

    const updatePricingDataForNewProperties = (oldPricingData, newPropertiesData) => {
        if (!oldPricingData || !oldPricingData.propertyData) return null;
        
        const updatedPropertyData = {};
        // بررسی ایمن برای زمانی که کاربر هیچ ویژگی‌ای ثبت نکرده است
        const isNowSimpleProduct = !newPropertiesData || !newPropertiesData.subProps1 || newPropertiesData.subProps1.length === 0;

        if (isNowSimpleProduct) {
            let oldMainProduct = oldPricingData.propertyData["کالای اصلی"];
            
            if (!oldMainProduct) {
                const firstKey = Object.keys(oldPricingData.propertyData)[0];
                if (firstKey && oldPricingData.propertyData[firstKey].length > 0) {
                    oldMainProduct = [{ 
                        ...oldPricingData.propertyData[firstKey][0], 
                        name: "بدون ویژگی",
                    }];
                }
            } else {
                oldMainProduct = oldMainProduct.map(item => ({ ...item, name: "بدون ویژگی" }));
            }
            
            if (oldMainProduct) {
                updatedPropertyData["کالای اصلی"] = oldMainProduct;
            }
            
            return { 
                ...oldPricingData, 
                propertyData: updatedPropertyData,
                propertiesData: newPropertiesData 
            };
        }

        const isSingleProperty = !newPropertiesData.subProps2 ||
            newPropertiesData.subProps2.length === 0 ||
            (newPropertiesData.subProps2.length === 1 && newPropertiesData.subProps2[0] === "");
            
        const oldKeys = Object.keys(oldPricingData.propertyData);

        newPropertiesData.subProps1.forEach((property1, p1Index) => {
            const p2List = isSingleProperty ? [property1] : newPropertiesData.subProps2;
            let oldPropertyItems = oldPricingData.propertyData[property1];
            
            if (!oldPropertyItems && oldKeys[p1Index] && oldKeys[p1Index] !== "کالای اصلی") {
                oldPropertyItems = oldPricingData.propertyData[oldKeys[p1Index]];
            } else if (!oldPropertyItems && oldPricingData.propertyData["کالای اصلی"]) {
                oldPropertyItems = oldPricingData.propertyData["کالای اصلی"];
            }

            if (oldPropertyItems) {
                const newPropertyItems = [];
                p2List.forEach((property2, p2Index) => {
                    let existingItem = null;
                    if (isSingleProperty) {
                        existingItem = oldPropertyItems[0];
                    } else {
                        existingItem = oldPropertyItems.find(item => item.name === property2);
                        if (!existingItem && oldPropertyItems[p2Index]) existingItem = oldPropertyItems[p2Index];
                    }
                    if (existingItem) {
                        newPropertyItems.push({ ...existingItem, name: property2 });
                    } else {
                        newPropertyItems.push({ name: property2, active: true, price: "", rawPrice: "", stock: "" });
                    }
                });
                updatedPropertyData[property1] = newPropertyItems;
            } else {
                updatedPropertyData[property1] = p2List.map(name => ({ name, active: true, price: "", rawPrice: "", stock: "" }));
            }
        });
        
        return { ...oldPricingData, propertyData: updatedPropertyData, propertiesData: newPropertiesData };
    };

    const progressPercent = (stepIndex / (steps.length - 1)) * 100;

    const LoadingOverlay = () => (
        <div className="pd-loading-overlay">
            <div className="pd-loading-spinner"></div>
            <div className="pd-loading-text">در حال بارگذاری اطلاعات محصول...</div>
        </div>
    );

    return (
        <Modal eventToggle={() => { }} isOpen={isOpen} locked={true} fullscreen={true}>
            <div className="pd-safe-area-container">
                <TelegramSafeArea style={{ background: "#ffffff", color: "#000", flexShrink: 0, width: "100%", borderBottom: "1px solid #f3f4f6" }}>
                    <div className="safe-area-title" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: '1.3', padding: '5px 0' }}>
                        <span style={{ fontSize: '15px', fontWeight: 'bold' }}>سبد 1</span>
                        <span style={{ fontSize: '12px', opacity: 0.85 }}>فروشگاه آنلاین</span>
                    </div>
                </TelegramSafeArea>

                <div id="my-main-modal" className="productdefinition-root" dir="rtl">
                    <div className="productdefinition-wrapper">
                        {isLoading && <LoadingOverlay />}

                        <div className="pd-title-bar">
                            <div className="pd-main-wrraper">
                                <div className="pd-title-container">
                                    <h1 className="pd-main-title">ثبت کالا</h1>
                                </div>
                                <div className="pd-exit-section" onClick={handleExit}>
                                    <span className="pd-exit-icon"><IoMdArrowRoundBack size={"30px"} /></span>
                                </div>
                            </div>
                        </div>

                        <div className="pd-progress">
                            <div className="pd-progress-line">
                                <div className="pd-progress-line-active" style={{ width: `${progressPercent}%` }}></div>
                            </div>

                            <div className="pd-progress-steps">
                                {steps.map((step, index) => {
                                    const isCompleted = completedSteps.includes(index) || index < stepIndex;
                                    const isActive = index === stepIndex;
                                    const isIncomplete = !isCompleted && !isActive;

                                    return (
                                        <div
                                            key={index}
                                            className={`pd-progress-step ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''} ${isIncomplete ? 'incomplete' : ''}`}
                                            onClick={() => goToStep(index)}
                                        >
                                            <div className={`pd-step-circle ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''} ${isIncomplete ? 'incomplete' : ''}`}>
                                                {step.icon}
                                                {isCompleted && (
                                                    <div className="pd-step-badge">
                                                        <IoMdCheckmark size={10} />
                                                    </div>
                                                )}
                                            </div>
                                            <span className="pd-step-label">{step.label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="pd-content">
                            {isLoading ? (
                                <div className="pd-content-loading">
                                    <div className="pd-content-spinner"></div>
                                    <p>در حال آماده‌سازی فرم...</p>
                                </div>
                            ) : (
                                <>
                                    {stepIndex === 0 && (
                                        <Page1Product
                                            onNext={handleProductData}
                                            onPrev={goPrev}
                                            isFirst={stepIndex === 0}
                                            isLast={stepIndex === steps.length - 1}
                                            initialData={productData}
                                            business_id={business_id}
                                        />
                                    )}

                                    {stepIndex === 1 && (
                                        <Page2Properties
                                            onNext={handlePropertiesData}
                                            onPrev={handlePropertiesDataOnPrev}
                                            initialData={propertiesData}
                                            business_id={business_id}
                                        />
                                    )}

                                    {stepIndex === 2 && (
                                        <Page3Properties
                                            onNext={handlePricingData}
                                            onPrev={handlePricingDataOnPrev}
                                            propertiesData={propertiesData}
                                            initialPricingData={pricingData}
                                        />
                                    )}

                                    {stepIndex === 3 && (
                                        <Page4Pictures
                                            onNext={(data) => {
                                                // --- قانون تعداد تصاویر ---
                                                const imgCount = data?.images?.length || 0;
                                                if (imgCount < 1) {
                                                    alert("لطفاً حداقل یک تصویر برای کالا انتخاب کنید.");
                                                    return;
                                                }
                                                if (imgCount > 5) {
                                                    alert("شما حداکثر مجاز به انتخاب ۵ تصویر هستید.");
                                                    return;
                                                }
                                                // در صورت معتبر بودن:
                                                setImagesData(data);
                                                if (data.deletedImages) setDeletedImages(data.deletedImages);
                                                if (!completedSteps.includes(3)) setCompletedSteps(prev => [...prev, 3]);
                                                goNext();
                                            }}
                                            onPrev={goPrev}
                                            initialData={imagesData}
                                            initialDeletedImages={deletedImages}
                                            onDataUpdate={handleImagesDataUpdate}
                                            onDeletedImagesUpdate={handleDeletedImagesUpdate}
                                            business_id={business_id}
                                        />
                                    )}

                                    {stepIndex === 4 && (
                                        <FinalStep
                                            productData={productData}
                                            propertiesData={propertiesData}
                                            pricingData={pricingData}
                                            imagesData={imagesData}
                                            deletedImages={deletedImages}
                                            onPrev={goPrev}
                                            onSubmit={(result) => {
                                                if (!completedSteps.includes(4)) setCompletedSteps(prev => [...prev, 4]);
                                            }}
                                            onNewProduct={handleNewProduct}
                                            business_id={business_id}
                                            onClose={handleExit}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
